// Primary Chinese Data Service
// Fetches data via A2A backend with Supabase fallback

import chineseDataService from '../services/chineseDataService';
import supabase from '../services/supabaseClient';

// Cache for loaded data
let cachedData = null;
let isLoading = false;
let loadPromise = null;

/**
 * Transform Supabase record to app format
 */
const transformRecord = (record) => {
  return {
    title: record.title,
    description: record.description,
    objectives: record.objectives,
    focus: record.focus,
    readingTypes: record.reading_types || [],
    languageUsage: record.language_usage || []
  };
};

/**
 * Combine Part1 and Part2 data for a level
 */
const combineLevelData = (part1, part2) => {
  if (!part1 && !part2) return null;
  
  const base = part1 || part2;
  const combined = {
    ...base,
    languageUsage: [
      ...(part1?.languageUsage || []),
      ...(part2?.languageUsage || [])
    ]
  };
  
  return combined;
};

/**
 * Load all primary Chinese data from Supabase
 */
export const loadPrimaryChineseData = async () => {
  // Return cached data if available
  if (cachedData) {
    return cachedData;
  }

  // Return existing promise if already loading
  if (isLoading && loadPromise) {
    return loadPromise;
  }

  // Start loading
  isLoading = true;
  loadPromise = (async () => {
    try {
      // Use chineseDataService (A2A with Supabase fallback)
      console.log('[PrimaryChineseData] Loading data via chineseDataService...');
      const combinedData = await chineseDataService.getPrimaryChineseData();
      
      if (!combinedData) {
        console.warn('[PrimaryChineseData] No primary Chinese data found');
        return null;
      }

      cachedData = combinedData;
      console.log('[PrimaryChineseData] Successfully loaded data via chineseDataService');
      return combinedData;
    } catch (error) {
      console.error('[PrimaryChineseData] Error loading primary Chinese data:', error);
      throw error;
    } finally {
      isLoading = false;
      loadPromise = null;
    }
  })();

  return loadPromise;
};

/**
 * Get data for a specific grade
 */
export const getGradeData = async (grade) => {
  const allData = await loadPrimaryChineseData();
  return allData?.[grade] || null;
};

/**
 * Get all primary Chinese data (for backward compatibility)
 */
export const getPrimaryChineseData = async () => {
  return await loadPrimaryChineseData();
};

/**
 * Clear cache (useful for testing or refreshing data)
 */
export const clearCache = () => {
  cachedData = null;
  isLoading = false;
  loadPromise = null;
};

// For backward compatibility - export a promise that resolves to the data structure
// This allows existing code to still work but will need to await the result
export const primaryChineseData = new Proxy({}, {
  get: (target, prop) => {
    console.warn('primaryChineseData is now async. Use getGradeData() or loadPrimaryChineseData() instead.');
    return null;
  }
}); 